import React, { useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { AudioFeature } from '../../../types/audioFeature';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';
import { Playlist } from '../playlist/Playlist';

import { AudioFeedbackFooter } from './AudioFeedbackFooter';
import { AudioHeaderPanel } from './AudioHeaderPanel';
import { AudioPlayerError } from './AudioPlayerError';
import { AudioTimeIndicator } from './AudioTimeIndicator';
import { MiniplayerControls } from './controls/MiniplayerControls';
import { OverflowMenu } from './menu/OverflowMenu';

const i18n = defineMessages({
	closeButtonLabel: {
		id: 'audio.audio-player.close-player-button',
		defaultMessage: 'Close audio player',
		description: 'The label used for the X icon to close the audio player',
	},
});

const styles = cssMap({
	expandedAudioPlayerContainer: {
		width: '380px',
		minHeight: '470px',
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	buttonsContainer: {
		paddingInline: token('space.050'),
	},
	controlsContainer: {
		height: '404px',
	},
});

type ExpandedAudioPlayerProps = {
	error?: Error;
	audioRef: React.RefObject<HTMLAudioElement>;
	audioFeatures: AudioFeature[];
};

// Error is a temporary prop - remove once implemented in the audio player store
export const ExpandedAudioPlayer = ({
	error,
	audioRef,
	audioFeatures,
}: ExpandedAudioPlayerProps) => {
	const { formatMessage } = useIntl();
	const [activeFeature, setActiveFeature] = useState<AudioFeature>(audioFeatures[0]);

	const { closeAudioPlayer } = useAudioPlayerStore();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const hasOverflowMenu =
		activeFeature.menuConfig?.items && activeFeature.menuConfig.items.length > 0;

	return (
		<Box
			xcss={styles.expandedAudioPlayerContainer}
			style={{
				background: `linear-gradient(180deg, ${token('elevation.surface')} 50%, ${token('elevation.surface.sunken')} 100%)`,
			}}
		>
			<Stack spread="space-between" xcss={styles.controlsContainer}>
				<Stack space="space.250" alignInline="center">
					<Inline
						alignInline="end"
						alignBlock="center"
						xcss={styles.buttonsContainer}
						grow="fill"
						spread={hasOverflowMenu ? 'space-between' : undefined}
					>
						{hasOverflowMenu && <OverflowMenu items={activeFeature.menuConfig!.items} />}
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={CrossIcon}
							label={formatMessage(i18n.closeButtonLabel)}
							onClick={closeAudioPlayer}
						/>
					</Inline>
					<AudioHeaderPanel
						featureList={audioFeatures}
						activeFeature={activeFeature}
						setActiveFeature={setActiveFeature}
					/>
					{error ? (
						<Flex justifyContent="center">
							<AudioPlayerError onRetry={() => {}} />
						</Flex>
					) : (
						<>
							<MiniplayerControls audioRef={audioRef} />
							{streamingState.state !== AudioStreamingState.UNINITIALIZED && (
								<AudioTimeIndicator audioRef={audioRef} />
							)}
						</>
					)}
				</Stack>
				<Box>
					<AudioFeedbackFooter />
				</Box>
			</Stack>
			<Playlist audioRef={audioRef} />
		</Box>
	);
};
